import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '../../config/locales';
import { useLanguage } from '../../contexts/LanguageContext';

const MARK = 'data-modamai-route-seo';

function siteBase() {
  const fromEnv = (process.env.REACT_APP_SITE_URL || '').trim().replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return 'https://modamai.com';
}

function localizedPath(routePath, loc) {
  const p = routePath.startsWith('/') ? routePath : `/${routePath}`;
  if (loc.code === DEFAULT_LOCALE) return p;
  if (p === '/') return `/${loc.urlCode}`;
  return `/${loc.urlCode}${p}`;
}

function homeJsonLd(base) {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'ModaMai',
      url: base,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'ModaMai',
      url: base,
      potentialAction: {
        '@type': 'SearchAction',
        target: { '@type': 'EntryPoint', urlTemplate: `${base}/search?q={search_term_string}` },
        'query-input': 'required name=search_term_string',
      },
    },
  ];
}

/**
 * Per-route canonical, hreflang cluster, og:url, and home JSON-LD (mirrors scripts/prerender.js).
 */
export default function GlobalRouteSeo() {
  const location = useLocation();
  const { getPathWithoutLocale, locale, current } = useLanguage();

  useEffect(() => {
    const path = location.pathname || '/';
    if (/^\/product\/[^/]+/.test(path) || /^\/blog\/[^/]+/.test(path) || /^\/collection\/[^/]+/.test(path)) {
      return undefined;
    }

    const base = siteBase();
    const routePath = getPathWithoutLocale(location.pathname) || '/';
    const altSuffix = routePath === '/' ? '' : routePath;
    const loc = current || SUPPORTED_LOCALES.find((l) => l.code === locale) || SUPPORTED_LOCALES[0];
    const canonicalPath = localizedPath(routePath, loc);
    const canonical = canonicalPath === '/' ? `${base}/` : `${base}${canonicalPath}`;

    document.querySelectorAll(`head [${MARK}="1"]`).forEach((el) => el.remove());

    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', canonical);
    link.setAttribute(MARK, '1');
    document.head.appendChild(link);

    const og = document.createElement('meta');
    og.setAttribute('property', 'og:url');
    og.setAttribute('content', canonical);
    og.setAttribute(MARK, '1');
    document.head.appendChild(og);

    for (const l of SUPPORTED_LOCALES) {
      const href =
        l.code === DEFAULT_LOCALE
          ? altSuffix
            ? `${base}${altSuffix}`
            : `${base}/`
          : `${base}/${l.urlCode}${altSuffix}`;
      const alt = document.createElement('link');
      alt.setAttribute('rel', 'alternate');
      alt.setAttribute('hreflang', l.hreflang || l.code);
      alt.setAttribute('href', href);
      alt.setAttribute(MARK, '1');
      document.head.appendChild(alt);
    }

    const xDefault = altSuffix ? `${base}${altSuffix}` : `${base}/`;
    const xAlt = document.createElement('link');
    xAlt.setAttribute('rel', 'alternate');
    xAlt.setAttribute('hreflang', 'x-default');
    xAlt.setAttribute('href', xDefault);
    xAlt.setAttribute(MARK, '1');
    document.head.appendChild(xAlt);

    if (routePath === '/') {
      for (const obj of homeJsonLd(base)) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute(MARK, '1');
        script.textContent = JSON.stringify(obj);
        document.head.appendChild(script);
      }
    }

    return () => {
      document.querySelectorAll(`head [${MARK}="1"]`).forEach((el) => el.remove());
    };
  }, [location.pathname, getPathWithoutLocale, locale, current]);

  return null;
}
